import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0404 from "../lesson-04/lesson";
import lesson0418 from "../lesson-18/lesson";
import lesson0420 from "../lesson-20/lesson";
const linkLesson0404 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0404.id);
const linkLesson0418 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0418.id);
const linkLesson0420 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0420.id);

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>The previous Lesson presented the theoretical formalism for modelling the behaviour of sealed enclosures. This type of system blocks the rear wave but does not make use of it. By means of an acoustic resonant circuit formed by a port and a volume, vented enclosures (also known as bass-reflex systems) exploit the rear wave, bringing it in phase with the front wave [4, 5]. This produces a virtuous cooperation between cone and port radiation that, although is only effective within a limited bandwidth, is sufficient to provide more low frequency output than a sealed enclosure of the same volume. Therefore compared to a sealed enclosure and when properly designed, vented enclosures may provide more low frequency output with a smaller enclosure. However, the speaker will be more sensitive to where it is placed inside the room, may add distortion due to turbulence in the vent, and is more complicated to correctly tune.
<h3 class=sectionHead>Vented-box principle</h3>
<p>
   The loudspeaker is mounted on an enclosure of volume
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>, which opens to the outside through a tube of length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   and surface
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   (see <a href=# data-img-alt="Figure 1. Vented-box system." data-img-src=${config.ASSETS_URL}/04/21/theory/figure-1.svg>Figure 1</a>). The tube behaves as an acoustic mass that, together with the volume
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>, forms a Helmholtz resonator (see <a href="${linkLesson0404}" target="_blank">Lesson 4</a>), with resonant frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>. The basic principle of the vented-box system is to extend the low frequency response of the loudspeaker by means of a carefully tuned acoustic resonator. The airflow generated by the rear of the diaphragm is then amplified by the resonator and radiated through the tube. The coupling between the loudspeaker and the enclosure allows the radiated pressure at low frequencies to be increased compared to a closed-box system of the same volume. The system must be carefully designed to achieve the desired response.
<p>
   Unlike in sealed enclosures, the rear wave should not be damped. Therefore, no absorbent material or, at most a thin lining to reduce standing waves, is employed.
   <figure class=figure>
<p><img alt="Figure 1. Vented-box system." src=${config.ASSETS_URL}/04/21/theory/figure-1.svg><figcaption class=caption>Figure 1. Vented-box system.</figcaption></figure>
<h3 class=sectionHead>Behavioural equations</h3>
<p>In order to model the bass-reflex system, the following assumptions are made:
<ul class=itemize1>
   <li class=itemize>the modelling is carried out at low frequencies: the loudspeaker and the enclosure are described by associations of equivalent electrical, mechanical or acoustic lumped components,
   <li class=itemize>
      the enclosure is modelled by an acoustic compliance of value
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ab</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>∕</mo>
         <mi>ρ</mi>
         <msup>
            <mrow>
               <mi>c</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </math>,
   <li class=itemize>
      the port is modelled by an acoustic mass of value
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ab</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mi>ρ</mi>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>∕</mo>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
      </math>, with
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mi>l</mi>
         <mo class=MathClass-bin>+</mo>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>f</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>, where
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>l</mi>
      </math>
      corresponds to the physical length and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>f</mi>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>
      respectively refer to the free and baffled correction lengths (see <a href="${linkLesson0404}" target="_blank">Lesson  4</a>),
   <li class=itemize>
      losses due to absorbent material are neglected
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ab</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>≃</mo>
         <mn>0</mn>
      </math>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mo>(</mo><msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>→</mo>
         <mi>∞</mi>
          <mo>)</mo>
      </math>,
   <li class=itemize>
      box losses are dominated by leakage, which mainly depend on the box size (the larger, the more losses). These can be modelled by a resistance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
      </math>. As
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
      </math>
      is difficult to estimate, box losses are often represented by the quality factor
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
      </math>, which can vary between
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mn>3</mn>
      </math>
      for large enclosures to
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>→</mo>
         <mi>∞</mi>
      </math>
      for small and perfectly sealed cabinets made of perfectly rigid walls. For simplicity, let’s assume
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>→</mo>
         <mi>∞</mi>
      </math>
      unless otherwise stated,
   <li class=itemize>the loudspeaker is driven by an electrical signal of sufficiently low amplitude so that all the involved physical phenomena remain linear,
   <li class=itemize>we suppose that the baffle is sufficiently large so that the radiation impedances of the loudspeaker’s diaphragm are those of the planar baffled piston.
</ul>
<p>Let’s start by recalling the coupling equations of the electrodynamic loudspeaker mounted in a planar baffle
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo form=prefix fence=true>{</mo>
               <mrow>
                  <mtable style="" equalcolumns=false align=axis class=array equalrows=false>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>U</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mi>I</mi>
                           <mo class=MathClass-bin>+</mo>
                           <mi mathvariant=italic>Bℓ</mi>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi mathvariant=italic>BℓI</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mn>2</mn>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ar</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   For the speaker mounted in the vented enclosure, the main difference is in the rear acoustic load. Thus, the volume flow created by the rear of the diaphragm splits into two parts: one part that flows into the volume of the enclosure and the other that exits through the vent and radiates sound to the outside. Then, the acoustic impedance seen from the back of the diaphragm corresponds to that of an open tube 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mo>(</mo>
      <mi mathvariant=italic>jω</mi>
       <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ap</mi>
         </mrow>
      </msub>
       <mo>)</mo>
   </math> in parallel with that of the enclosure 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
       <mo>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ab</mi>
         </mrow>
      </msub>
       <mo>)</mo>
   </math>. The system of coupled equations becomes
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo form=prefix fence=true>{</mo>
               <mrow>
                  <mtable style="" equalcolumns=false align=axis class=array equalrows=false>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>U</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mi>I</mi>
                           <mo class=MathClass-bin>+</mo>
                           <mi mathvariant=italic>Bℓ</mi>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi mathvariant=italic>BℓI</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mrow>
                              <mo form=prefix fence=true>(</mo>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi mathvariant=italic>ar</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mfrac>
                                    <mrow>
                                       <mi mathvariant=italic>jω</mi>
                                       <msub>
                                          <mrow>
                                             <mi>M</mi>
                                          </mrow>
                                          <mrow>
                                             <mi mathvariant=italic>ap</mi>
                                          </mrow>
                                       </msub>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mo class=MathClass-bin>−</mo>
                                       <msup>
                                          <mrow>
                                             <mi>ω</mi>
                                          </mrow>
                                          <mrow>
                                             <mn>2</mn>
                                          </mrow>
                                       </msup>
                                       <msub>
                                          <mrow>
                                             <mi>C</mi>
                                          </mrow>
                                          <mrow>
                                             <mi mathvariant=italic>ab</mi>
                                          </mrow>
                                       </msub>
                                       <msub>
                                          <mrow>
                                             <mi>M</mi>
                                          </mrow>
                                          <mrow>
                                             <mi mathvariant=italic>ap</mi>
                                          </mrow>
                                       </msub>
                                    </mrow>
                                 </mfrac>
                              </mrow>
                              <mo form=postfix fence=true>)</mo>
                           </mrow>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   is the radiation impedance loading the front side of the membrane and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ap</mi>
         </mrow>
      </msub>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ab</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ap</mi>
         </mrow>
      </msub>
   </math>
   is the impedance loading the rear side. The resolution of the system (2) allows the calculation of the electrical impedance or the pressure response of the system. This will be done in the following sections.
<h3 class=sectionHead>Electrical impedance</h3>
<p>
   It is possible to obtain an electrical circuit equivalent to the system of equations (2) (see <a href=# data-img-src=${config.ASSETS_URL}/04/21/theory/figure-2.svg data-img-alt="Figure 2. Equivalent electrical circuit of a vented-box system.">Figure 2</a>). To do this, first the acoustic part is brought into the mechanical domain and then the mechanical domain into the electrical domain. We can then obtain the expression for the electrical impedance of the bass-reflex speaker
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ebr</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>U</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>I</mi>
   </math>
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ebr</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>es</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>es</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>es</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>eb</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mfrac>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>jω</mi>
                              <msub>
                                 <mrow>
                                    <mi>C</mi>
                                 </mrow>
                                 <mrow>
                                    <mi mathvariant=italic>ep</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>where:
<ul class=itemize1>
   <li class=itemize>
      the elements
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>es</mi>
            </mrow>
         </msub>
      </math>
      ,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>es</mi>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>es</mi>
            </mrow>
         </msub>
      </math>
      corresponds to the mechanical impedance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ms</mi>
            </mrow>
         </msub>
      </math>
      (see <a href="${linkLesson0418}" target="_blank">Lesson 18</a>) of a baffled speaker brought in the electrical domain:
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>es</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>B</mi>
               <msup>
                  <mrow>
                     <mi>ℓ</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>R</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>ms</mi>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>es</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mi>B</mi>
         <msup>
            <mrow>
               <mi>ℓ</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ms</mi>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>es</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>M</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>ms</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <mi>B</mi>
               <msup>
                  <mrow>
                     <mi>ℓ</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
      </math>,
   <li class=itemize>
      the elements
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>eb</mi>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ep</mi>
            </mrow>
         </msub>
      </math>
      respectively corresponds to the acoustical impedances of the volume and vent brought in the electrical domain:
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>eb</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>B</mi>
               <msup>
                  <mrow>
                     <mi>ℓ</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
               <msub>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>ab</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msubsup>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mi>d</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msubsup>
            </mrow>
         </mfrac>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ep</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>M</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>ap</mi>
                  </mrow>
               </msub>
               <msubsup>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mi>d</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msubsup>
            </mrow>
            <mrow>
               <mi>B</mi>
               <msup>
                  <mrow>
                     <mi>ℓ</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
      </math>.
</ul>
<figure class=figure>
   <img src=${config.ASSETS_URL}/04/21/theory/figure-2.svg alt="Figure 2. Equivalent electrical circuit of a vented-box system.">
   <figcaption class=caption>Figure 2. Equivalent electrical circuit of a vented-box system.</figcaption>
</figure>
<figure class=figure>
      <img alt="Figure 3. Equivalent impedance of a bass-reflex system." src=${config.ASSETS_URL}/04/21/theory/figure-3.svg>
      <figcaption class=caption>Figure 3. Equivalent impedance of a bass-reflex system.</figcaption>
</figure>
<p>
   <a href=# data-img-alt="Figure 3. Equivalent impedance of a bass-reflex system." data-img-src=${config.ASSETS_URL}/04/21/theory/figure-3.svg>Figure 3</a> compares the electrical impedance of a loudspeaker in a baffle with that of the same driver mounted in a vented-box enclosure (calculated using Eq. 3). In this case, both the speaker and the enclosure are chosen to have the same resonance frequency 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
       <mo>(</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
       <mo>)</mo>
   </math> and the volume of the enclosure is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>30</mn>
      <mi>%</mi>
   </math>
   lower than the equivalent volume of the loudspeaker 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo>(</mo>
       <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>as</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mi>α</mi>
   </math>
   , with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>3</mn>
      <mo class=MathClass-close>)</mo>
   </math>. While the impedance of the baffled speaker has only one peak, that of the bass-reflex system has two. This is because the loudspeaker and the cabinet are two coupled resonators, resulting in a system with two degrees of freedom. We also note that the two resonances are located on either side of the tuning frequency of the vented-box system (here
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>).
<h3 class=sectionHead>On-axis response</h3>
<p>
   To obtain the radiated pressure of the vented-box system, it is necessary to calculate both the acoustic volume velocity rates
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   of the diaphragm and vent respectively. This can be done by solving the system of coupled equations (2) or by modifying the equivalent circuit for the closed cabinet (see <a href="${linkLesson0420}" target="_blank">Lesson 20</a>) to take the vent into account. This latter is done in <a href=# data-img-src=${config.ASSETS_URL}/04/21/theory/figure-4.svg data-img-alt="Figure 4. Equivalent circuit of a vented-box system in the acoustical domain.">Figure 4</a> for a lossless enclosure. From this circuit we can calculate
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>q</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   as well as the total flow radiated by the system
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>. The expression of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   is given by
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>g</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mn>4</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>4</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mn>4</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msub>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mn>1</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   with
   <span style="font-size: 14px">
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msqrt>
               <mrow>
                  <mi>h</mi>
               </mrow>
            </msqrt>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ts</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
      <mstyle class=mbox>
         <mtext>, </mtext>
          <mspace width="5px">
      </mstyle>
      <msub>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>h</mi>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <mi>α</mi>
         </mrow>
         <mrow>
            <mi>h</mi>
         </mrow>
      </mfrac>
      <mstyle class=mbox>
          <mspace width="5px">
         <mtext> and </mtext>
            <mspace width="5px">
      </mstyle>
      <msub>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ts</mi>
               </mrow>
            </msub>
            <msqrt>
               <mrow>
                  <mi>h</mi>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
   </math>
   </span>, where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math> and  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ω</mi>
        </mrow>
        <mrow>
            <mn>0</mn>
        </mrow>
    </msub>
      <mo class=MathClass-rel>=</mo>
        <mrow>
            <mn>2</mn>
            <mi>π</mi>
        </mrow>
        <msqrt>
               <mrow>
                        <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
               </mrow>
                  <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
        </msqrt>
   </math>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/21/theory/figure-4.svg alt="Figure 4. Equivalent circuit of a vented-box system in the acoustical domain."><figcaption class=caption>Figure 4. Equivalent circuit of a vented-box system in the acoustical domain.</figcaption></figure>
<p>
   An example is given on <a href=# data-img-alt="Figure 5. Volume velocities of a vented-box system." data-img-src=${config.ASSETS_URL}/04/21/theory/figure-5.svg>Figure 5</a> for the parameters given in <a href=# data-img-alt="Table 1. Parameters used for the vented-box simulation." data-img-src=${config.ASSETS_URL}/04/21/theory/table-1.svg>Table 1</a>, note that for this system the chosen tuning is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>. We note that the vibratory behaviour of the diaphragm is strongly modified by the coupling: there are two maxima distributed on both sides of the resonant frequency of the loudspeaker. For
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo>(</mo><mi>f</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
       <mo>)</mo>
   </math>, the membrane is almost blocked but the little radiated energy is largely amplified by the acoustic resonator: the flow of the vent is very large in this frequency region. We also notice that the total flow rate
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   is lower than the flow rate of the vent as long as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   because
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   are in phase opposition. For higher frequencies the two flows are in phase.
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Parameters used for the vented-box simulation.</figcaption>
      <img alt="Table 1. Parameters used for the vented-box simulation." src=${config.ASSETS_URL}/04/21/theory/table-1.svg>
   </figure>
</div>
<figure class=figure>
   <p>
      <img alt="Figure 5. Volume velocities of a vented-box system." src=${config.ASSETS_URL}/04/21/theory/figure-5.svg>
      <figcaption class=caption>Figure 5. Volume velocities of a vented-box system.</figcaption>
</figure>
<p>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   can be used and to calculate the radiated pressure using a monopole model
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mi>k</mi>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mstyle class=mbox>
               <mtext> and </mtext>
            </mstyle>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mi>k</mi>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   are respectively the distance from the calculation point to the diaphragm and the vent. If the membrane and the vent are close enough compared to the calculation distance we can assume that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>r</mi>
   </math>, so the total pressure is then given by
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>p</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mi>k</mi>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mi>k</mi>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(6)
</table>
<p>Then, the on-axis pressure is given by
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ax</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>q</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>BlU</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>G</mi>
               </mrow>
               <mrow>
                  <mi>v</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>with
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>G</mi>
               </mrow>
               <mrow>
                  <mi>v</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>4</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mn>4</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>4</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mn>4</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msub>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mn>4</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mn>1</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   This response corresponds to that of a 4<sup class=textsuperscript><span class=cmr-9>th</span></sup> order high-pass filter, with a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>24</mn>
   </math>
    dB per octave slope. The on-axis pressure level for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
    W for the system whose parameters are listed in <a href=# data-img-alt="Table 1. Parameters used for the vented-box simulation." data-img-src=${config.ASSETS_URL}/04/21/theory/table-1.svg>Table 1</a> is given in <a href=# data-img-alt="Figure 6. On-axis pressure levels for <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>1</mn></math> W at <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>1</mn></math> m of a vented-box system." data-img-src=${config.ASSETS_URL}/04/21/theory/figure-6.svg>Figure 6</a>. The pressure radiated around
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>, is mainly due to the Helmholtz resonator as the membrane is almost blocked. At very low frequencies, as the pressure fields coming from the diaphragm and the vent are almost out of phase, the total radiated pressure is very low.
   <figure class=figure>
<p><img alt="Figure 6. On-axis pressure levels for <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>1</mn></math> W at <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>1</mn></math> m of a vented-box system." src=${config.ASSETS_URL}/04/21/theory/figure-6.svg><figcaption class=caption>Figure 6. On-axis pressure levels for <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>1</mn></math> W at <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>1</mn></math> m of a vented-box system.</figcaption></figure>
<h3 class=sectionHead>Design process</h3>
<h4 class=subsectionHead>Finding an appropriate alignment</h4>
<p>
   Designing a vented enclosure is not an easy task. The difficulty relies on the fact that it is a system with two degrees of freedom. As such, the design consists of choosing a particular combination of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>, that we refer to as an alignment. However, not all alignments provide a satisfactory frequency response, as illustrated in <a href=# data-img-alt="Figure 7. Frequency response of different alignments." data-img-src=${config.ASSETS_URL}/04/21/theory/figure-7.svg>Figure 7</a>. As a result, this is a trial and error process until we reach the desired response. As stated in the previous chapter, frequency response can be assessed by means of lower cutoff frequency and ripple. Fortunately, there are many available Computer-Aided Design (CAD) tools to assist in the design process. Commonly, they have driver’s parameters,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>
   as input parameters and provide the frequency response as result.
   <figure class=figure>
<p><img alt="Figure 7. Frequency response of different alignments." src=${config.ASSETS_URL}/04/21/theory/figure-7.svg><figcaption class=caption>Figure 7. Frequency response of different alignments.</figcaption></figure>
<h4 class=subsectionHead>Cone excursion and maximum linear SPL</h4>
<p>
   From the analysis of the equivalent circuit, it can be inferred that the displacement exhibits a minimum at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   (that tends to null for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>→</mo>
      <mi>∞</mi>
   </math>) and that below
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   it rises as if no acoustic suspension existed (i.e. as the driver mounted on an infinite baffle). This occurs because below
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   the acoustic mass of the port is the dominant element of the Helmholtz resonator. Nevertheless, as the audio signal can be filtered below the lower cutoff frequency, the important matter is that the cone displacement does not exceed
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal><msub><mi>X</mi><mi>max</mi></msub></mstyle>
   </math>
   within the working frequency range of the system (see <a href=# data-img-alt="Figure 8. Example of performance limits for linear behaviour in a vented enclosure employing a driver with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><msub><mi>X</mi><mi>max</mi></msub></mstyle><mo class=MathClass-rel>=</mo><mn>10</mn></math> mm. Input level was limited so that cone displacement (left) did not exceed <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><msub><mi>X</mi><mi>max</mi></msub></mstyle></math> within the working frequency range. SPL output (right) corresponding to that input level is represented." data-img-src=${config.ASSETS_URL}/04/21/theory/figure-8.svg>Figure 8</a> left). Otherwise, audible distortion is likely to appear.
<p>
   The linear excursion limit imposed by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
     <mstyle mathvariant=normal><msub><mi>X</mi><mi>max</mi></msub></mstyle>
   </math>
   will limit, in turn, the maximum linear output level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mstyle mathvariant=normal><msub><mi>SPL</mi><mi>max</mi></msub></mstyle>
   </math>. Therefore, even more important than assessing the SPL produced at “
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>83</mn>
   </math>
    V”, <em><span class=cmti-10>i.e.</span></em>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
    W for a 8 Ohm load, is to assess the maximum SPL achievable while staying in the linear region (see <a href=# data-img-alt="Figure 8. Example of performance limits for linear behaviour in a vented enclosure employing a driver with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><msub><mi>X</mi><mi>max</mi></msub></mstyle><mo class=MathClass-rel>=</mo><mn>10</mn></math> mm. Input level was limited so that cone displacement (left) did not exceed <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><msub><mi>X</mi><mi>max</mi></msub></mstyle></math> within the working frequency range. SPL output (right) corresponding to that input level is represented." data-img-src=${config.ASSETS_URL}/04/21/theory/figure-8.svg>Figure 8</a> right).
   <figure class=figure>
<p><img alt="Figure 8. Example of performance limits for linear behaviour in a vented enclosure employing a driver with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><msub><mi>X</mi><mi>max</mi></msub></mstyle><mo class=MathClass-rel>=</mo><mn>10</mn></math> mm. Input level was limited so that cone displacement (left) did not exceed <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><msub><mi>X</mi><mi>max</mi></msub></mstyle></math> within the working frequency range. SPL output (right) corresponding to that input level is represented." src=${config.ASSETS_URL}/04/21/theory/figure-8.svg><figcaption class=caption>Figure 8. Example of performance limits for linear behaviour in a vented enclosure employing a driver with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><msub><mi>X</mi><mi>max</mi></msub></mstyle><mo class=MathClass-rel>=</mo><mn>10</mn></math> mm. Input level was limited so that cone displacement (left) did not exceed <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><msub><mi>X</mi><mi>max</mi></msub></mstyle></math> within the working frequency range. SPL output (right) corresponding to that input level is represented.</figcaption></figure>
<h4 class=subsectionHead>Port dimensions</h4>
<p>Port dimensions are mainly given by two parameters: section area and length. The section area is an important feature, as it has a direct impact on air speed. From fluid dynamics theory [3], to keep the air flow constant, the air speed increases as the section area decreases, which may produce audible noise. On the contrary, larger section areas require higher port lengths, hence occupying larger volumes and producing higher resonance peaks in the frequency response. As a result, either with small or large pipes we will run into limitations to be dealt with.
<p>
   The air velocity within the port can be obtained by dividing the volumetric velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   (at maximum linear SPL) by the cross-section of the port:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mstyle mathvariant=normal>
                     <mi>air</mi>
                  </mstyle>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   <a href=# data-img-alt="Figure 9. Port air speed for the the speaker 18NBX100 on a vented enclosure at Maximum achievable SPL for port diameters of <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>10</mn></math> cm, <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>20</mn></math> cm and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>50</mn></math> cm." data-img-src=${config.ASSETS_URL}/04/21/theory/figure-9.svg>Figure 9</a> shows an example of the port air speed for different port diameters. It is noticeable how air speed decreases as port diameter increases (there is an inverse relationship). In order to avoid audible noise, air speed should not exceed
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>25</mn>
   </math>
    m/s. According to this criterion, the target port diameter of the example should be set to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>20</mn>
   </math>
    cm.
   <figure class=figure>
<p><img alt="Figure 9. Port air speed for the the speaker 18NBX100 on a vented enclosure at Maximum achievable SPL for port diameters of <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>10</mn></math> cm, <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>20</mn></math> cm and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>50</mn></math> cm." src=${config.ASSETS_URL}/04/21/theory/figure-9.svg><figcaption class=caption>Figure 9. Port air speed for the the speaker 18NBX100 on a vented enclosure at Maximum achievable SPL for port diameters of <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>10</mn></math> cm, <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>20</mn></math> cm and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>50</mn></math> cm.</figcaption></figure>
<p>
   The port length that satisfies the required
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   can be then obtained from the following equation, which involves the port diameter, the box volume and the Helmholtz frequency
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>16</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>b</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>b</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ec</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ec</mi>
         </mrow>
      </msub>
   </math>
   is an end correction factor that depends on the port termination, which can be either sharp or flared (<a href=# data-img-alt="Figure 10. Port termination can be either sharp (left) or flared (right)." data-img-src=${config.ASSETS_URL}/04/21/theory/figure-10.svg>Figure 10</a>). For sharp port ends
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mstyle mathvariant=normal>
               <mi>ec</mi>
            </mstyle>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>732</mn>
   </math>
   should be used, whereas in the case of flared termination
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mstyle mathvariant=normal>
               <mi>ec</mi>
            </mstyle>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   is suggested. Flared ports improve performance in terms of air turbulence and linearity at high SPL values, while providing a better aesthetic appearance.
   <figure class=figure>
<p><img alt="Figure 10. Port termination can be either sharp (left) or flared (right)." src=${config.ASSETS_URL}/04/21/theory/figure-10.svg><figcaption class=caption>Figure 10. Port termination can be either sharp (left) or flared (right).</figcaption></figure>
<h4 class=subsectionHead>Defining target parameters</h4>
<p>
   An important question for a designer to address: the target parameters to be achieved. If you wish lower cutoff frequency about
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>30</mn>
   </math>
    Hz and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>125</mn>
   </math>
    dB SPL with a pocket-size device, simply forget about it: the perfect loudspeaker does not exist. Due to the trade-offs between bass response, SPL
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=normal>
      <msub>
      <mi>SPL</mi>
      <mi>max</mi>
      </msub>
      </mstyle>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>, reasonable values to these parameters should be assigned. The application to which the loudspeaker is intended matters to answer this question, and a different balance between performance parameters is required for HiFi, Public Address or portable applications, among other. The designer should then assign priorities to sound pressure, size and frequency response. This is a kind of dividing a small pie into three portions of not necessarily equal size. Therefore, an important job of the designer is to define the size of each portion. <a href=# data-img-alt="Figure 11. Suggesting performance profiles and range values for loudspeakers target parameters depending on the application." data-img-src=${config.ASSETS_URL}/04/21/theory/figure-11.svg>Figure 11</a> gives some hints for performance profiles depending on the application.
   <figure class=figure>
<p><img alt="Figure 11. Suggesting performance profiles and range values for loudspeakers target parameters depending on the application." src=${config.ASSETS_URL}/04/21/theory/figure-11.svg><figcaption class=caption>Figure 11. Suggesting performance profiles and range values for loudspeakers target parameters depending on the application.</figcaption></figure>
<h4 class=subsectionHead>The importance of driver selection</h4>
<p>A common peculiarity to all enclosure designs is that the transducer parameters will strongly constrain the achievable performance profile of the speaker system. In other words, the selection of the transducer is a key step to which special attention must be paid. For the non-experienced user, an iterative procedure involving a set of transducers should be evaluated in order to choose the most appropriate driver. Instead of performing this job manually, which is time consuming, you can be assisted by LoVE-BASS [2], a tool for driver selection based on a backwards approach which can be freely downloaded from github [1].
<h3 class=sectionHead>Conclusion</h3>
<p>Compared to a closed-box system of the same size, the vented-box system has the two main following advantages:
<ul class=itemize1>
   <li class=itemize>a lower cut-off frequency. Indeed, the frequency response is extended if the system is properly designed,
   <li class=itemize>a lower distortion as the diaphragm is vibrating less at low frequencies,
   <li class=itemize>the vent provides DC pressure equalisation meaning that temperature fluctuations inside the enclosure will be less likely to damage the speaker.
</ul>
<p>However, bass-reflex enclosures also exhibit some limitations:
<ul class=itemize1>
   <li class=itemize>for the same quality factor value, a 2<sup class=textsuperscript><span class=cmr-9>nd</span></sup> order high pass filter (sealed enclosure) has a shorter impulse, and less ripple, than a 4<sup class=textsuperscript><span class=cmr-9>th</span></sup> order (bass reflex enclosure),
   <li class=itemize>at high sound pressure levels, the velocity in the vent can become very high then producing turbulent noise and blowing sounds. The latter comes from standing waves in the longitudinal direction of the port which are excited by vortices,
   <li class=itemize>poorly tunes systems may produce excessive pressure at the tuning frequency relative to the rest of the pass-band,
   <li class=itemize>higher acoustical modes of the enclosure may be radiated to the listening room,
   <li class=itemize>the position of the port will have an impact on where the loudspeaker may be placed in the listening room. The port mouth should not be placed too close to walls or furniture.
</ul>
<p>Therefore, bass-reflex systems must be well designed to reduce their limitations and take full advantage of the smaller box volume.
<h3 class=likesectionHead>Glossary</h3>
<p>
<dl>
  <dt>CAD
  <dd>Computer-Aided Design.
  <dt>DC
  <dd>Direct Current.
  <dt>HiFi
  <dd>High Fidelity.
  <dt>SPL
  <dd>Sound Pressure Level.
  <dt>SPL<sub>max</sub>
  <dd>Maximum achievable SPL.
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-Castellsgithub class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Francisco Castells and José Manuel Requena. <span class=cmti-10>Loudspeakers for Vented </span><span class=cmti-10>Enclosures A Backward Approach for Speaker Selection (LoVE BASS)</span>. <a href=https://github.com/jmrplens/LoVE-BASS target="_blank" class=url><span class=cmtt-10>https://github.com/jmrplens/LoVE-BASS</span></a>. 2019.
   <dt id=X0-castells2019loudspeakers class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Francisco Castells and José Manuel Requena. “Loudspeakers for Vented Enclosures A Backward Approach for Speaker Selection (LoVE BASS)”. In: <span class=cmti-10>Voice Coil </span>32.9 (2019), pp. 18–21.
   <dt id=X0-roozen1998vortex class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>N Bert Roozen et al. “Vortex sound in bass-reflex ports of loudspeakers. Part I. Observation of response to harmonic excitation and remedial measures”. In: <span class=cmti-10>The Journal of the Acoustical Society of America </span>104.4 (1998), pp. 1914–1918.
   <dt id=X0-small1973venteda class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>
      <p>Richard H Small. “Vented-box loudspeaker systems–part 1: Small-signal analysis”. In: <span class=cmti-10>Journal of the Audio Engineering Society </span>21.5 (1973), pp. 363–372.
   <dt id=X0-small1973ventedb class=thebibliography>[5]
   <dd id=bib-5 class=thebibliography>
      <p>Richard H Small. “Vented-box loudspeaker systems-part 2: Large-signal analysis”. In: <span class=cmti-10>Journal of the audio engineering society </span>21.6 (1973), pp. 438–444.
</dl>`;

export default theory;
